import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AddIcon from '@mui/icons-material/Add';
import { styled } from '@mui/material';
import { TextField } from '@calefy-inc/informedMaterial';
import { ArrayField, useArrayFieldItemState, useArrayFieldApi } from 'informed';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import type { DropResult } from 'react-beautiful-dnd';
import {
  withFormStyles,
  WithFormStyles,
} from '../../../../../../util/withFormStyles';
import {
  WhiteLabelSectionDivider,
  WhiteLabelSectionRoot,
} from './whiteLabelSectionCommon';

interface CustomButtonsSectionProps extends WithFormStyles {}

export const CustomButtonsSection = withFormStyles()(
  function CustomButtonsSection({ classes }: CustomButtonsSectionProps) {
    return (
      <WhiteLabelSectionRoot>
        <Typography variant='h6' gutterBottom>
          Custom Sidebar Buttons
        </Typography>
        <WhiteLabelSectionDivider />
        <SectionHint variant='caption' color='text.secondary'>
          Drag the grip icon to change the order shown to clients.
        </SectionHint>

        <ArrayField name='customButtons'>
          {({ addWithInitialValue, swap }) => (
            <>
              <CustomButtonsDragList
                textfieldClassName={classes.textfield}
                arrayFieldSwap={swap}
              />

              <AddRowButton
                startIcon={<AddIcon />}
                onClick={() => addWithInitialValue({ title: '', url: '' })}
                variant='outlined'
                size='small'
              >
                Add Button
              </AddRowButton>
            </>
          )}
        </ArrayField>
      </WhiteLabelSectionRoot>
    );
  },
);

function CustomButtonsDragList({
  textfieldClassName,
  arrayFieldSwap,
}: {
  textfieldClassName: string;
  arrayFieldSwap: (a: number, b: number) => void;
}) {
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    let i = result.source.index;
    const to = result.destination.index;
    // RBD can move by many rows; Informed only has `swap(i,i±1)` (keeps row keys in sync).
    while (i !== to) {
      if (i < to) {
        arrayFieldSwap(i, i + 1);
        i += 1;
      } else {
        arrayFieldSwap(i, i - 1);
        i -= 1;
      }
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId='white-label-custom-buttons'>
        {(dropProvided) => (
          <DroppableListRoot
            ref={dropProvided.innerRef}
            {...dropProvided.droppableProps}
          >
            <ArrayField.Items>
              {() => (
                <CustomButtonItem textfieldClassName={textfieldClassName} />
              )}
            </ArrayField.Items>
            {dropProvided.placeholder}
          </DroppableListRoot>
        )}
      </Droppable>
    </DragDropContext>
  );
}

/** Single row: drag handle + fields (order follows list index; not edited manually). */
function CustomButtonItem({
  textfieldClassName,
}: {
  textfieldClassName: string;
}) {
  const { index, key } = useArrayFieldItemState();
  const arrayFieldApi = useArrayFieldApi();
  const draggableId = String(key);

  return (
    <Draggable draggableId={draggableId} index={index}>
      {(dragProvided, snapshot) => (
        <DraggableButtonRow
          ref={dragProvided.innerRef}
          {...dragProvided.draggableProps}
          $isDragging={snapshot.isDragging}
          // RBD positions the row via transform; inline style is required by the library
          style={dragProvided.draggableProps.style}
        >
          <DragHandleButton
            size='small'
            {...dragProvided.dragHandleProps}
            aria-label='Drag to reorder'
          >
            <DragIndicatorIcon fontSize='small' />
          </DragHandleButton>
          <TextField
            name='title'
            label='Button Title'
            variant='standard'
            size='small'
            fullWidth
            className={textfieldClassName}
          />
          <TextField
            name='url'
            label='URL'
            variant='standard'
            size='small'
            fullWidth
            className={textfieldClassName}
          />
          <IconButton
            onClick={() => arrayFieldApi.remove(index)}
            size='small'
            color='error'
            aria-label='Remove button'
          >
            <DeleteIcon />
          </IconButton>
        </DraggableButtonRow>
      )}
    </Draggable>
  );
}

const SectionHint = styled(Typography)(({ theme }) => ({
  display: 'block',
  marginBottom: theme.spacing(1),
}));

const AddRowButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1),
}));

const DroppableListRoot = styled(Box)(({ theme }) => ({
  minHeight: theme.spacing(0.5),
}));

const DraggableButtonRow = styled(Box, {
  shouldForwardProp: (prop) => prop !== '$isDragging',
})<{ $isDragging: boolean }>(({ theme, $isDragging }) => ({
  display: 'grid',
  gridTemplateColumns: 'auto 1fr 2fr auto',
  gap: theme.spacing(2),
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: $isDragging ? theme.palette.action.hover : undefined,
}));

const DragHandleButton = styled(IconButton)({
  cursor: 'grab',
});
